import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Languages, LogOut, Menu } from 'lucide-react';
import type { NoticeDto } from '../../../domain/contracts/api.contracts';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { useLocaleStore } from '../../../application/stores/localeStore';
import { Badge } from '../ui/Badge';
import { NoticesPopover } from './NoticesPopover';
import './TopBar.css';

export interface TopBarProps {
  userName: string;
  tierLabel: string;
  unreadNotices?: number;
  notices?: NoticeDto[];
  isLoadingNotices?: boolean;
  onLogout: () => void;
  onToggleMenu: () => void;
  onNavigateNotices?: () => void;
}

export function TopBar({
  userName,
  tierLabel,
  unreadNotices,
  notices,
  isLoadingNotices = false,
  onLogout,
  onToggleMenu,
  onNavigateNotices,
}: TopBarProps) {
  const { t } = useTranslation();
  const toggleLocale = useLocaleStore((s) => s.toggleLocale);
  const navigate = useNavigate();
  const [isNoticesOpen, setIsNoticesOpen] = useState(false);
  const bellWrapperRef = useRef<HTMLDivElement>(null);

  const noticesList = notices ?? [];
  const effectiveUnread = unreadNotices ?? noticesList.length;

  useEffect(() => {
    if (!isNoticesOpen) return;
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (bellWrapperRef.current && !bellWrapperRef.current.contains(e.target as Node)) {
        setIsNoticesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isNoticesOpen]);

  const handleNavigateAll = () => {
    setIsNoticesOpen(false);
    if (onNavigateNotices) {
      onNavigateNotices();
    } else {
      navigate('/notices');
    }
  };

  return (
    <header className="topbar">
      <button className="tap topbar-icon show-mobile" onClick={onToggleMenu} aria-label={t('nav.menu')}>
        <Menu size={22} />
      </button>
      <div className="topbar-brand">
        <span className="topbar-logo" aria-hidden>
          স
        </span>
        <div className="topbar-user">
          <strong>{userName}</strong>
          <span className="muted">{tierLabel}</span>
        </div>
      </div>
      <div className="topbar-actions">
        <button className="tap topbar-icon" onClick={toggleLocale} aria-label="Switch language" data-testid="locale-toggle">
          <Languages size={20} />
        </button>
        <div ref={bellWrapperRef} className="topbar-bell-wrapper">
          <button
            type="button"
            className={`tap topbar-icon topbar-bell${isNoticesOpen ? ' active' : ''}`}
            onClick={() => setIsNoticesOpen((prev) => !prev)}
            aria-label={t('nav.notices')}
            aria-expanded={isNoticesOpen}
            aria-haspopup="dialog"
            data-testid="topbar-bell-button"
          >
            <Bell size={20} />
            {effectiveUnread > 0 && (
              <Badge tone="danger">
                <span data-testid="unread-badge">{effectiveUnread}</span>
              </Badge>
            )}
          </button>
          {isNoticesOpen && (
            <NoticesPopover
              notices={noticesList}
              isLoading={isLoadingNotices}
              onClose={() => setIsNoticesOpen(false)}
              onNavigateAll={handleNavigateAll}
            />
          )}
        </div>
        <button className="tap topbar-icon" onClick={onLogout} aria-label={t('nav.logout')} data-testid="logout-button">
          <LogOut size={20} />
        </button>
      </div>
    </header>
  );
}
